/**
 * ═══════════════════════════════════════════════════════════════════════
 * Auth Error Utilities
 * ═══════════════════════════════════════════════════════════════════════
 *
 * Maps backend error codes to user-friendly messages.
 */

// STAFF-DEVICE-LOCK / FE-2 — owner-ruled verbatim copy (R-DL-5). Single
// constant so DEVICE_NOT_APPROVED and DEVICE_ID_MISSING can never drift
// into two different sentences. No error code / jargon / screen name.
const DEVICE_DENIED_MESSAGE = "This device isn't approved for your account. Ask your admin to approve it.";

const ERROR_CODE_MESSAGES = {
  USER_NOT_FOUND: 'No account found. Please sign up first.',
  USER_ALREADY_EXISTS: 'An account with this identifier already exists. Please log in instead.',
  INVALID_OTP: 'Invalid OTP. Please check and try again.',
  OTP_EXPIRED: 'OTP has expired. Please request a new one.',
  OTP_NOT_FOUND: 'OTP not found or expired. Please request a new one.',
  OTP_MAX_ATTEMPTS: 'Too many failed attempts. Please request a new OTP.',
  PASSWORD_WEAK: 'Password is too weak. Use at least 8 characters with uppercase, lowercase, number, and symbol.',
  PASSWORD_POLICY_VIOLATION: 'Password does not meet requirements. Use at least 8 characters with uppercase, lowercase, number, and symbol.',
  USER_TYPE_MISMATCH: 'This account is registered under a different user type. Please select the correct one.',
  MISSING_FIELDS: 'Please fill in all required fields.',
  VALIDATION_ERROR: 'Invalid input. Please check your details.',
  AUTH_FAILED: 'Invalid email/phone or password. Please try again.',
  UNAUTHORIZED: 'Session expired. Please log in again.',
  SESSION_REQUIRED: 'Your session has expired. Please log in again to set your password.',
  SESSION_EXPIRED: 'Your session has expired. Please log in again to set your password.',
  TOO_MANY_REQUESTS: 'Too many requests. Please wait a moment and try again.',
  INTERNAL_ERROR: 'Something went wrong. Please try again later.',
  BACKEND_UNAVAILABLE: 'Due to a temporary issue, we are unable to sign you in. Please try again.',
  DEVICE_NOT_APPROVED: DEVICE_DENIED_MESSAGE,
  DEVICE_ID_MISSING: DEVICE_DENIED_MESSAGE,
};

/**
 * Extract a user-friendly message from an error thrown by authApi.
 *
 * @param {Error} error
 * @returns {string}
 */
export function getAuthErrorMessage(error) {
  // If we have a structured code, use the mapping
  if (error.code && ERROR_CODE_MESSAGES[error.code]) {
    return ERROR_CODE_MESSAGES[error.code];
  }

  // Fallback to message heuristics
  const msg = (error.message || '').toLowerCase();
  if (msg.includes('expired')) return ERROR_CODE_MESSAGES.OTP_EXPIRED;
  if (msg.includes('too many')) return ERROR_CODE_MESSAGES.OTP_MAX_ATTEMPTS;
  if (msg.includes('invalid') && msg.includes('otp')) return ERROR_CODE_MESSAGES.INVALID_OTP;
  if (msg.includes('mismatch')) return ERROR_CODE_MESSAGES.USER_TYPE_MISMATCH;
  if (msg.includes('already exists')) return ERROR_CODE_MESSAGES.USER_ALREADY_EXISTS;
  if (msg.includes('not found')) return ERROR_CODE_MESSAGES.USER_NOT_FOUND;

  return error.message || 'An unexpected error occurred. Please try again.';
}
